'use client';

import React, { useEffect, useRef } from 'react';
import { LuChevronRight } from 'react-icons/lu';
import type { UiSearchResult } from '@search/core/SearchResultMapper';
import { decodeAzureWikiName, looksLikeAzureWikiEntry } from '@core/helpers/markdown/utils/wikiNaming';

interface SearchResultsProps {
  results: UiSearchResult[];
  selectedIndex: number;
  onResultClick: (result: UiSearchResult) => void;
  query: string;
}

function escapeRegex(str: string) { return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); }
function highlightNodes(text: string, query: string): React.ReactNode[] {
  if (!query || !query.trim()) return [text];
  const terms = query
    .toLowerCase()
    .split(/\s+/)
    .filter((t) => t.length > 1)
    .map((t) => escapeRegex(t));
  if (terms.length === 0) return [text];
  const regex = new RegExp(`\\b(${terms.join('|')})\\b`, 'gi');
  const parts = text.split(regex);
  const out: React.ReactNode[] = [];
  for (let i = 0; i < parts.length; i++) {
    const chunk = parts[i];
    if (i % 2 === 1) {
      out.push(
        <mark key={i} className='bg-yellow-200 dark:bg-yellow-800 px-0.5 rounded'>
          {chunk}
        </mark>,
      );
    } else if (chunk) {
      out.push(<React.Fragment key={i}>{chunk}</React.Fragment>);
    }
  }
  return out.length ? out : [text];
}

function getVaultColor(vaultSlug: string): string {
  const colors = {
    articles: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    projects: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    zenaxiom: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
  };

  return colors[vaultSlug as keyof typeof colors] || 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
}

function formatRelativePath(path: string): string {
  try {
    const cleaned = path.replace(/\.(md|mdx)$/i, '');
    const parts = cleaned.split('/').filter((p) => p && !p.startsWith('_'));
    const decoded = parts.map((p) => {
      if (looksLikeAzureWikiEntry(p)) return decodeAzureWikiName(p);
      if (/%[0-9A-Fa-f]{2}/.test(p)) {
        try { return decodeURIComponent(p); } catch { /* ignore */ }
      }
      return p;
    });
    return decoded.join(' › ');
  } catch {
    return path;
  }
}

export function SearchResults({ results, selectedIndex, onResultClick, query }: SearchResultsProps) {
  const maxScore = Math.max(0, ...results.map((r) => (Number.isFinite(r.score) ? r.score : 0)));

  function labelForScore(r: UiSearchResult): { label: string; className: string; raw: number } {
    const raw = Number.isFinite(r.score) ? r.score : 0;
    const ratio = maxScore > 0 ? raw / maxScore : 0;
    const percent = Number.isFinite(ratio) ? Math.min(100, Math.max(0, ratio * 100)) : 0;
    if (percent >= 85) {
      return { label: 'Excellent', className: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200', raw };
    }
    if (percent >= 60) {
      return { label: 'Strong', className: 'bg-lime-100 text-lime-800 dark:bg-lime-900 dark:text-lime-200', raw };
    }
    if (percent >= 35) {
      return { label: 'Good', className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200', raw };
    }
    if (percent >= 15) {
      return { label: 'Fair', className: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200', raw };
    }
    return { label: 'Related', className: 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200', raw };
  }

  function formatScore(raw: number): string {
    if (!Number.isFinite(raw) || raw === 0) return '0';
    if (Math.abs(raw) >= 1) return raw.toFixed(2);
    if (Math.abs(raw) >= 0.01) return raw.toFixed(3);
    if (Math.abs(raw) >= 0.0001) return raw.toPrecision(2);
    return raw.toExponential(2);
  }

  const listRef = useRef<HTMLDivElement | null>(null);
  const itemRefs = useRef<Array<HTMLDivElement | null>>([]);

  useEffect(() => {
    if (!Array.isArray(results) || results.length === 0) return;
    const target = itemRefs.current[selectedIndex];
    if (!target) return;
    try {
      target.scrollIntoView({ block: 'nearest' });
    } catch {
      // ignore
    }
  }, [results, selectedIndex]);

  const showDebugScores =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1' ||
      window.location.hostname === '::1');

  return (
    <div ref={listRef} className='max-h-96 overflow-y-auto overflow-x-hidden w-full'>
      {results.map((result, index) => {
        const isSelected = index === selectedIndex;
        const vaultColor = getVaultColor(result.vaultSlug);
        const formattedPath = formatRelativePath(result.relativePath || '');

        return (
          <div
            key={result.id}
            ref={(el) => {
              itemRefs.current[index] = el;
            }}
            className={`min-w-0 w-full box-border p-4 border-b border-gray-100 dark:border-gray-700 cursor-pointer transition-colors ${
              isSelected
                ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
                : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
            }`}
            onClick={() => onResultClick(result)}>
            <div className='flex items-start gap-2'>
              {/* Content */}
              <div className='flex-1 min-w-0'>
                {/* Heading */}
                <div className='flex items-center gap-2 mb-1 min-w-0 flex-wrap'>
                  <h3 className='font-medium text-gray-900 dark:text-gray-100 block min-w-0 max-w-full overflow-hidden break-words line-clamp-2'>
                    {highlightNodes(result.heading || '', query)}
                  </h3>
                  {result.level && result.level > 1 && (
                    <span className='text-xs text-gray-500 dark:text-gray-400'>H{result.level}</span>
                  )}
                </div>

                {/* Snippet */}
                {result.snippet && (
                  <p className='text-sm text-gray-600 dark:text-gray-300 line-clamp-2 mb-2'>
                    {highlightNodes(result.snippet || '', query)}
                  </p>
                )}

                {/* Metadata */}
                <div className='flex items-center gap-2 text-xs flex-wrap'>
                  {/* Vault badge */}
                  <span className={`px-2 py-1 rounded-full font-medium ${vaultColor}`}>{result.vaultName || result.vaultSlug}</span>

                  {/* Path */}
                  {formattedPath && (
                    <>
                      <LuChevronRight className='w-3 h-3 text-gray-400' />
                      <span className='text-gray-500 dark:text-gray-400 truncate max-w-full'>{formattedPath}</span>
                    </>
                  )}

                  {/* Relevance label + numeric score */}
                  {maxScore > 0 && (
                    <>
                      <LuChevronRight className='w-3 h-3 text-gray-400' />
                      {(() => {
                        const tag = labelForScore(result);
                        return (
                          <span className={`px-1.5 py-0.5 rounded ${tag.className}`}>
                            {tag.label}
                            <span className='ml-1 text-[10px] opacity-60 align-middle font-mono'>
                              {formatScore(tag.raw)}
                            </span>
                          </span>
                        );
                      })()}
                    </>
                  )}
                  {/* Debug scores */}
                  {showDebugScores && (
                    <>
                      <LuChevronRight className='w-3 h-3 text-gray-400' />
                      <span className='text-gray-400 font-mono'>
                        {result.match.strategy || 'bm25'}:{' '}
                        {result.match.bm25 !== undefined ? `b=${result.match.bm25.toFixed(2)} ` : ''}
                        {result.match.sem !== undefined ? `s=${result.match.sem.toFixed(2)} ` : ''}
                        → {result.score.toFixed(2)}
                      </span>
                    </>
                  )}
                </div>

                {/* Tags from metadata */}
                {result.metadata.tags && result.metadata.tags.length > 0 && (
                  <div className='flex items-center gap-1 mt-2'>
                    {result.metadata.tags.slice(0, 3).map((tag: string) => (
                      <span
                        key={tag}
                        className='px-1.5 py-0.5 text-xs bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded'>
                        {tag}
                      </span>
                    ))}
                    {result.metadata.tags.length > 3 && (
                      <span className='text-xs text-gray-500 dark:text-gray-400'>
                        +{result.metadata.tags.length - 3} more
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Selected indicator */}
              {isSelected && (
                <div className='flex-shrink-0 mt-1'>
                  <LuChevronRight className='w-4 h-4 text-blue-500' />
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
